import { cloneArray } from "./base";
import { resolveBinding, resolveItemRegexpBinding } from "./bindingUtils";
import { selectImmutable } from "../selectors/selectByBindTo";
import { getCurrentDashboardId } from "../actions/Dashboard";
import { WEBUILogs } from "@tinqin/tinqin-utils/src/logging";

const DEFAULT_WIDGET_PATH = "{any}";

const getWidgetPath = (token, widgetId, state) => {
    let fullWidgetPath = token;
    fullWidgetPath = fullWidgetPath.replace("{self}", widgetId);
    if (fullWidgetPath.includes("{parent}")) {
        let parentWidget = state.getIn(["data", widgetId, "parentWidget"]);
        if (parentWidget) {
            fullWidgetPath = fullWidgetPath.replace("{parent}", parentWidget);
        } else {
            fullWidgetPath = fullWidgetPath.replace("{root}", parentWidget);
        }
    }
    fullWidgetPath = fullWidgetPath.replace("{root}", getCurrentDashboardId(state));
    //unique widgetId
    if (!token.includes(".")) {
        state.get("data").find((value, key) => {
            if (key === fullWidgetPath || key.endsWith("." + fullWidgetPath)) {
                fullWidgetPath = key;
                return true;
            }
            return false;
        });
    }
    return fullWidgetPath;
};

export const parsePath = (path, state, originEntityPath, widgetId) => {
    let widgetPath = DEFAULT_WIDGET_PATH;
    let entityPath = path;
    if (path.includes("::")) {
        let tokens = path.split("::");
        widgetPath = getWidgetPath(tokens[0], widgetId, state);
        entityPath = tokens[1];
    }
    entityPath = entityPathReplaceReservedWords(entityPath, widgetId, originEntityPath, state);
    return {
        widgetPath,
        entityPath
    };
};
//extract storePaths from dependencies
//dependencies can be entityPath, partial entityPath, entityClassName, relative entityPath
//dependencies can start with widgetPath::entityPath/partialEntityPath/entityClassName/relativeEntityPath
//returns array of storePaths
export function getDependentPaths(dependencies = [], state, originEntityPath, widgetId) {
    let resultStorePaths = [];
    let dependencyItems = dependencies.map(item =>
        parsePath(item, state, originEntityPath, widgetId)
    );

    dependencyItems.forEach(item => {
        let storePath;
        //extracts store path from entityPath/relativeEntityPath or entityClassName
        //in case of entityClassName multiple paths can be extracted
        storePath = extractStorePath(item.entityPath, state, originEntityPath, item.widgetPath);

        if (storePath.length) {
            //in case of entityClassName multiple paths can be extracted
            if (Array.isArray(storePath[0])) {
                resultStorePaths = resultStorePaths.concat(storePath);
            } else {
                resultStorePaths.push(storePath);
            }
        } else {
            WEBUILogs.warn("Unable to find entity: " + item.entityPath);
        }
    });

    // dependencies = checkForReservedWords(dependencies, widgetId, originEntityPath);
    // dependencies.forEach(dependencyItem => {
    //     let storePath;

    //     //extracts store path from entityPath/relativeEntityPath or entityClassName
    //     //in case of entityClassName multiple paths can be extracted
    //     storePath = extractStorePath(dependencyItem, state, originEntityPath);

    //     if (storePath.length) {
    //         //in case of entityClassName multiple paths can be extracted
    //         if (Array.isArray(storePath[0])) {
    //             resultStorePaths = resultStorePaths.concat(storePath);
    //         } else {
    //             resultStorePaths.push(storePath);
    //         }
    //     } else {
    //         console.warn("Unable to find entity: " + dependencyItem);
    //     }
    // });

    return resultStorePaths;
}

//for an entity path returns uiState store path
//returns an array of [widgetId, uiStatePath]
//accepts array of bindTo strings
export function getUiStatePaths(dependencies = [], state, originEntityPath, widgetId) {
    return dependencies.map(dependencyItem =>
        getUiStatePath(dependencyItem, state, originEntityPath, widgetId)
    );
}

//for an entity path returns uiState store path
//returns an array of [widgetId, uiStatePath]
export function getUiStatePath(path, state, originEntityPath, originWidgetId) {
    let { widgetPath, entityPath } = parsePath(path, state, originEntityPath, originWidgetId);
    let resultWidgetId = widgetPath;
    //resolving relative binding
    let resolvedPath = resolveBinding(entityPath, originEntityPath);
    //if no widget specified in path, find widgetId from entityPath
    if (widgetPath === DEFAULT_WIDGET_PATH) {
        let { widgetId } = getEntityPathWidget(state, resolvedPath);
        resultWidgetId = widgetId;
    }
    return [resultWidgetId, resolvedPath];
}

//extracts data for an entity by id/path/relativePath
//returns Immutable reference or undefined
export function extractEntityData(entityReference, state, originEntityPath) {
    let resultPath = extractStorePath(entityReference, state, originEntityPath);

    if (resultPath.length === 0) {
        WEBUILogs.error("Entity data not found - " + entityReference);
        return;
    }

    return state.getIn(["data", ...resultPath]);
}

//extracts store path from entityPath/relativeEntityPath or entityClassName
//in case of entityClassName multiple paths can be extracted
export function extractStorePath(
    entityReference = "",
    state,
    originEntityPath,
    widgetPath = DEFAULT_WIDGET_PATH
) {
    let entityRefPath = entityReference;
    if (isEntityPath(entityRefPath, state)) {
        if (isRelativeEntityPath(entityRefPath)) {
            return getStorePathsFromRelativeEntityPath(
                state,
                entityRefPath,
                originEntityPath,
                widgetPath
            );
        } else {
            return getStorePathFromEntityPath(state, entityRefPath, widgetPath);
        }
    } else {
        return getStorePathsFromEntityClassName(state, entityRefPath, widgetPath);
    }
}

function getStorePathFromEntityPath(store, entityPath, widgetPath) {
    let storePath = [];
    let widgetId;
    let widgetState;
    if (widgetPath === DEFAULT_WIDGET_PATH) {
        let { widgetId: widgetIdExtract, widgetState: widgetStateExtract } = getEntityPathWidget(
            store,
            entityPath
        );
        widgetId = widgetIdExtract;
        widgetState = widgetStateExtract;
    } else {
        widgetId = widgetPath;
        widgetState = store.getIn(["data", widgetId]);
    }

    //entityPath may not contain "."
    let entityPathSplit = entityPath.split(".") || [entityPath];
    //
    let searchResult = findInWidgetState(widgetState, entityPathSplit, storePath);
    if (searchResult) {
        //storePath may be empty in case of top level entity path (example: ["language"])
        return storePath.length ? [widgetId, ...storePath] : [widgetId];
    }
    return [];
}

//extract {widgetId, widgetState} by entityPath
export function getEntityPathWidget(store, path) {
    let widgetId = "";
    let widgetState;
    //entityPath may not contain "."
    let entityPathSplit = path.split(".") || [path];
    //find correct widget
    let widgetClassName = entityPathSplit[0];

    widgetState = store.get("data").find((value, key) => {
        let result;
        //add widget container logic -  do not contain class attribute
        let widgetClass = value.get("class");
        if (widgetClass) {
            result = widgetClass.includes(widgetClassName);
        }

        if (!result) {
            result = value.get("widget") === widgetClassName;
        }

        //save entity widgetId
        if (result) {
            widgetId = key;
        }
        return result;
    });
    return {
        widgetId: widgetId,
        widgetState: widgetState
    };
}

//uses originEntityPath to generate new entityPath
function getStorePathsFromRelativeEntityPath(
    state,
    relativePath,
    originEntityPath = "",
    widgetPath
) {
    let contextPathTokens;
    let newEntityPath;
    let contextPath;
    //contextPath should exclude origin entity
    //Example:
    //activityFlow.step1.details.bonus_malus ->  activityFlow.step1.details
    contextPath = originEntityPath.replace(/(.*)[.][^.]*$/g, "$1");

    // "./1/2/3" -> "contextPath/1/2/3"
    if (relativePath.startsWith("./")) {
        newEntityPath = relativePath.replace("./", contextPath + ".");
    } else {
        contextPathTokens = contextPath.split(".");

        //replace all "../"
        while (relativePath.includes("../")) {
            relativePath = relativePath.replace("../", "");
            contextPathTokens.pop();
        }

        //merging remaining contextPath with remaining relativePath
        newEntityPath = contextPathTokens.join(".") + (relativePath ? "." + relativePath : "");
    }
    return getStorePathFromEntityPath(state, newEntityPath, widgetPath);
}

//recursive DFS search inside state entity immutable map,
//for entities with searchClassName
//returns all storePaths for found dependencies
//storePaths: [widgetId, "entities", index1, "entities", index2, ...]
function getStorePathsFromEntityClassName(store, searchClassName, widgetPath) {
    let allStorePaths = [];
    if (widgetPath === DEFAULT_WIDGET_PATH) {
        let allWidgetStates = store.get("data");
        allWidgetStates.forEach((widgetEntity, widgetId) => {
            // widgetEntity.get("entities").find(
            //     findDependecies.bind(this,
            //         [widgetId], allStorePaths, searchClassName)
            // );
            //search inside each widget
            findDependecies([widgetId], allStorePaths, searchClassName, widgetEntity);
        });
    } else {
        findDependecies(
            [widgetPath],
            allStorePaths,
            searchClassName,
            store.getIn(["data", widgetPath])
        );
    }

    return allStorePaths;
}

//helper function for getStorePathsFromEntityClassName
function findDependecies(
    currentStorePath,
    allStorePaths,
    searchClassName,
    stateEntity,
    stateEntityIndex
) {
    //let entityId = getEntityId(stateEntity);
    let nestedEntities = stateEntity.get("entities");
    let stateEntityClasses = stateEntity.get("class") || [];
    let stateEntityWidgetId = stateEntity.get("widget") || "";
    //first widgetEntity has no index
    if (stateEntityIndex !== undefined) {
        currentStorePath.push(stateEntityIndex);
    }

    if (stateEntityClasses.includes(searchClassName)) {
        allStorePaths.push(cloneArray(currentStorePath));
    } else if (stateEntityWidgetId === searchClassName) {
        allStorePaths.push(cloneArray(currentStorePath));
    }

    if (nestedEntities) {
        currentStorePath.push("entities");

        nestedEntities.find(
            findDependecies.bind(this, currentStorePath, allStorePaths, searchClassName)
        );
        //clear currentStorePath at the end of the search inside the current subtree
        //once for "entities"
        currentStorePath.pop();
    }
    //first widgetEntity has no index
    if (stateEntityIndex !== undefined) {
        //clear currentStorePath at the end of the search inside the current subtree
        //once for entityIndex
        currentStorePath.pop();
    }
}

//returns entity id
//properties.id or first className inside class property
export function getEntityId(stateEntity) {
    //single id location should be used
    // //get properties.id
    // let result = stateEntity.getIn(["properties", "id"]);
    // //or class[0] as an id
    // result = result || stateEntity.getIn(["class", 0]);

    return stateEntity.getIn(["class", 0]);
}
function isEntityPath(entityPath = "", state) {
    if (entityPath.includes(".")) {
        return true;
    }

    let isTopLevelEntityName = false;
    state.get("data").forEach(widgetEntity => {
        let widgetClass = widgetEntity.get("class");
        if (widgetClass && widgetClass.includes(entityPath)) {
            isTopLevelEntityName = true;
        }
        if (widgetEntity.get("widget") === entityPath) {
            isTopLevelEntityName = true;
        }
        // if (widgetEntity.get("class").includes(entityPath)) {
        //     isTopLevelEntityName = true;
        // }
    });

    return isTopLevelEntityName;
}

function isRelativeEntityPath(entityPath = "") {
    return entityPath.startsWith(".");
}

//return  immutable Map by entityPath
//as a sideEffect set storePath to the store path of the entity
export function findInWidgetState(immutableState, path, storePath = []) {
    let currentPathNode;
    let stateClass;
    let classMatch;
    let noPath = !path || !path.length;
    let children;
    let matchedKey;

    if (!immutableState) {
        return false;
    }

    if (noPath) {
        return immutableState;
    }

    stateClass = immutableState.get("class");

    if (!stateClass) {
        return false;
    }

    currentPathNode = path.shift();
    classMatch = stateClass.includes(currentPathNode);
    let widgetIdMatch = immutableState.get("widget") === currentPathNode;

    if (!classMatch && !widgetIdMatch) {
        return false;
    }

    if (classMatch && !path.length) {
        return immutableState;
    }

    if (widgetIdMatch && !path.length) {
        return immutableState;
    }

    children = immutableState.get("entities");

    if (!children) {
        return false;
    } else {
        storePath.push("entities");

        matchedKey = children.findKey(value => {
            let childClasses = value.get("class") || [];

            return childClasses.includes(path[0]);
        });

        if (matchedKey !== undefined) {
            storePath.push(matchedKey);

            return findInWidgetState(children.get(matchedKey), path, storePath);
        }
    }

    return false;
}

//fast comparison of entities in immutable data
//using shallow comparison for all properties except "entities"
//inner entities compares by length and order only
export function compareImmutableStates(left, right) {
    let areEqual = true;

    if (left === right) {
        return true;
    }

    if (!left || !right) {
        return false;
    }

    if (left.size !== right.size) {
        return false;
    }
    //compare each property
    left.forEach((leftValue, key) => {
        //in case inner property is different: entities are different
        if (key !== "entities" && right.get(key) !== leftValue) {
            areEqual = false;
        }

        //inner entities are compared by length and order only no deep comparison
        if (key === "entities") {
            let rightInnerEntities = right.get(key);
            //null check, size check
            if (!rightInnerEntities || leftValue.size !== rightInnerEntities.size) {
                areEqual = false;
            } else {
                //order check
                leftValue.forEach((entityValue, entityKey) => {
                    let leftEntityId = getEntityId(entityValue);
                    let rightEntityId = getEntityId(rightInnerEntities.get(entityKey));

                    if (leftEntityId !== rightEntityId) {
                        areEqual = false;
                    }
                });
            }
        }
    });

    return areEqual;
}

//extracts entityPath from storePath
export function getEntityPath(storePath, state) {
    let result = [];
    let entityName;
    for (let i = 1; i <= storePath.length; i += 2) {
        //extracting each entity id
        entityName = getEntityId(state.getIn(["data", ...storePath.slice(0, i)]));
        result.push(entityName);
    }
    return result.join(".");
}

//extracts data for all the children
export function getChildrenData(parentProps, state) {
    let { widgetId = "", config = {}, data = {}, computedBindTo = "" } = parentProps;

    let childrenData = [];
    let configChildren = config.children && config.children.length;

    if (configChildren) {
        //resolve data for each child config
        config.children.forEach((item, index) => {
            let childData = selectImmutable(state, {
                bindTo: item.bindTo,
                widgetId: widgetId,
                parentBindTo: computedBindTo,
                index: index
            });
            childData.config = Object.assign({}, item);
            childData.widgetId = widgetId;
            childrenData.push(childData);
        });
    }

    if (config.item) {
        let entities = data && data.get ? data.get("entities") : [];
        let entitiesLength = entities ? entities.size : 0;
        //resolve data for each item in entities
        for (let index = 0; index < entitiesLength; ++index) {
            let childData = selectImmutable(state, {
                bindTo: config.item.bindTo,
                widgetId: widgetId,
                parentBindTo: computedBindTo,
                index: index
            });
            childData.config = Object.assign({}, config.item);
            childData.widgetId = widgetId;
            childrenData.push(childData);
        }
    }

    if (config.items) {
        let entities = data && data.get ? data.get("entities") : [];
        let entitiesLength = entities ? entities.size : 0;
        //resolve data for each item in entities
        for (let index = 0; index < entitiesLength; ++index) {
            let resolvedItems = [];
            config.items.forEach(item => {
                let resolvedItem = resolveItemRegexpBinding(
                    item,
                    entities.getIn([index, "class", 0])
                );
                if (resolvedItem && resolvedItem.item) {
                    resolvedItems.push(resolvedItem.item);
                }
            });
            if (resolvedItems.length > 0) {
                let childData = selectImmutable(state, {
                    bindTo: resolvedItems[0].bindTo,
                    widgetId: widgetId,
                    parentBindTo: computedBindTo,
                    index: index
                });
                childData.config = Object.assign({}, resolvedItems[0]);
                childData.widgetId = widgetId;
                childrenData.push(childData);
            }
        }
    }

    return childrenData;
}

export function checkForReservedWords(targets = [], widgetId, entityPath, state) {
    return targets.map(target =>
        entityPathReplaceReservedWords(target, widgetId, entityPath, state)
    );
}

function entityPathReplaceReservedWords(target, widgetPath, entityPath) {
    // if (target === "{root}" && widgetPath !== "{any}") {
    //     state.get("data").forEach((value, key) => {
    //         if (key === widgetPath) {
    //             target = value.getIn(["class", 0]);
    //         }
    //     });
    // }
    //TODO deprecate this
    if (target.includes("{root}.")) {
        let targetsSplit = target.split(".").shift();
        return targetsSplit.join(".");
    }
    //TODO deprecate this rule
    if (target.includes("{widget}")) {
        target = target.replace("{widget}", widgetPath);
    }
    if (target.includes("{self}")) {
        target = target.replace("{self}", entityPath);
    }
    return target;
}

//new contract - widgetId can be different by the widget class
export function getWidgetId(state, path, originEntityPath, currentWidgetId) {
    // let widgetId = false;
    let { widgetPath } = parsePath(path, state, originEntityPath, currentWidgetId);
    return widgetPath;
}
export function getWidgetClass(state, entityId) {
    let widgetClass = false;
    state.get("data").find(value => {
        let result;
        let widgetId = value.get("widget");
        if (widgetId && widgetId === entityId) {
            widgetClass = value.getIn(["class", 0]);
            result = true;
        }
        return result;
    });

    return widgetClass;
}
export function checkEntityIsWidget(state, entityId) {
    let widgetId = false;
    state.get("data").find((value, key) => {
        let result;
        //add widget container logic -  do not contain class attribute
        let widgetClass = value.get("class");
        if (widgetClass) {
            result = widgetClass.includes(entityId);
        }

        if (!result) {
            result = value.get("widget") === entityId;
        }

        //save entity widgetId
        if (result) {
            widgetId = key;
        }
        return result;
    });

    return widgetId;
}
//return widget id from storePath
export function getStorePathWidgetId(storePath = []) {
    return storePath[0];
}
